import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import Spinner from '../components/common/Spinner/Spinner';
import './CentralAjuda.css';

const TABS = [
    { id: 'gov-gestao', label: 'Sobre o Mago e o iESGo', file: null, isStatic: true },
    { id: 'registro-ip', label: 'Registro de Software & Autoria', file: null, isStatic: true },
    { id: 'termos', label: 'Termos de Uso', file: '/docs/TERMOS_DE_USO.md' },
    { id: 'privacidade', label: 'Política de Privacidade', file: '/docs/POLITICA_PRIVACIDADE.md' },
    { id: 'manual-admin', label: 'Manual do Administrador', file: '/docs/MANUAL_ADMINISTRADOR.md' },
    { id: 'manual-articulador', label: 'Manual do Articulador', file: '/docs/MANUAL_ARTICULADOR.md' },
    { id: 'guia-preenchimento', label: 'Guia de Preenchimento', file: '/docs/GUIA_PREENCHIMENTO.md' },
    { id: 'manual-publico', label: 'Guia de Visualização Pública', file: '/docs/MANUAL_VISUALIZACAO_PUBLICA.md' },
];

const GOV_GESTAO_CONTENT = `
# Sobre o Mago e o iESGo: Entendendo a Metodologia e o Sistema

Esta página detalha os fundamentos da metodologia de avaliação do Tribunal de Contas da União (TCU) e como a plataforma MAGO atua como a ferramenta tecnológica para viabilizar essa gestão na prática.

## O que é o iESGo?
O iESGo (Índice de Governança, Sustentabilidade e Gestão) é o indicador de referência do TCU para medir a maturidade administrativa das organizações públicas brasileiras. Ele substitui o antigo iGG, expandindo a análise para os pilares de sustentabilidade e responsabilidade social.

O índice funciona como um diagnóstico que avalia temas críticos como:

*   **Governança Organizacional:** Liderança, estratégia e controle.
*   **Gestão de Pessoas e TI:** Segurança da informação e desenvolvimento de talentos.
*   **Contratações e Orçamento:** Eficiência no uso do dinheiro público.
*   **Sustentabilidade (ESG):** Impacto ambiental e inclusão social.

## O que é o MAGO?
O MAGO é um sistema de gestão desenvolvido especificamente para apoiar instituições (como os IFES) na jornada de conformidade e excelência com o iESGo. Ele funciona como uma plataforma de front-end moderna integrada ao Firebase, permitindo a coleta, organização e visualização dos dados necessários para o diagnóstico.

### Objetivos do MAGO
O sistema foi projetado para descentralizar e profissionalizar a gestão da governança:

*   **Gestão Estruturada de Questões:** Permite manter um banco de dados atualizado com os critérios de avaliação do TCU, organizados por códigos hierárquicos e temas ESG.
*   **Monitoramento de Desempenho (KPIs):** Oferece dashboards globais e organizacionais que exibem indicadores consolidados, médias gerais e o status das respostas em tempo real.
*   **Colaboração entre Perfis:** Facilita a interação entre Administradores Master (que gerem o catálogo) e Articuladores (responsáveis por responder e prover evidências para a organização).
*   **Transparência e Auditoria:** Mantém um histórico de respostas e registros de atividades para garantir que a autoavaliação seja precisa e auditável.

## Como o MAGO interage com o iESGo?
A interação entre o software e a metodologia ocorre de forma técnica e estratégica:

*   **Vínculo com Dados Públicos:** No cadastro de uma organização, o MAGO permite associar indicadores do TCU, possibilitando a comparação entre o desempenho interno e os dados públicos oficiais.
*   **Ciclo de Diagnóstico:** O sistema permite a criação de questões baseadas no levantamento do TCU, possibilitando que a organização realize simulados e acompanhamentos contínuos, em vez de esperar o ciclo trienal do Tribunal.
*   **Exportação de Evidências:** O MAGO suporta a exportação de resultados em formatos PDF, CSV e XLSX, facilitando a prestação de contas e a análise de falhas apontadas pelo índice.
*   **Ações Corretivas:** Ao identificar áreas vulneráveis através dos gráficos do dashboard, os gestores podem utilizar o MAGO para priorizar ajustes em temas com baixa pontuação, elevando a saúde administrativa da instituição.

## Diferença de Métricas: Mago vs. TCU

Embora o sistema Mago e o TCU compartilhem o mesmo DNA na avaliação de governança, eles diferem na forma como consolidam seus resultados finais. É importante destacar que, no nível operacional, o cálculo do percentual de cumprimento de cada ação no Mago segue exatamente as métricas do TCU. Se uma prática exige evidências específicas para atingir 100% de conformidade perante o Tribunal, o Mago aplica o mesmo rigor técnico para validar essa entrega.

A divergência surge apenas no fechamento do Indicador Geral, onde cada ferramenta cumpre um propósito distinto:

### Mago: O Indicador Democrático (Visão Interna)
No Mago, a nota geral é calculada por média simples. Isso significa que todas as ações cadastradas possuem o mesmo peso no índice final. Essa escolha é proposital: busca-se uma visão interna e democrática do ESG, onde o esforço de cada equipe e cada projeto contribui de forma igualitária para o progresso da organização. É uma métrica de gestão que valoriza a execução e o engajamento de todos os setores.

### TCU: O Indicador de Risco (Visão Global)
Já o TCU utiliza médias ponderadas e algoritmos de peso para gerar o iESGo. Por ter uma visão abrangente e global, o Tribunal prioriza questões de liderança e controle estratégico. Para o TCU, certas ações têm "mais valor" que outras por reduzirem riscos institucionais maiores, criando um ranking de maturidade que permite comparar diferentes órgãos públicos em escala nacional.
`;

const REGISTRO_IP_CONTENT = `
# Propriedade Intelectual e Registro de Software

O **MAGO — Monitoramento de Ações de Governança** é um programa de computador devidamente registrado perante o Instituto Nacional da Propriedade Industrial (INPI), em conformidade com a legislação brasileira (Lei 9.609/1998).

## Dados do Registro
- **Processo Nº:** BR512024001217-6
- **Expedido em:** 24/04/2024
- **Título:** Mago - Monitoramento de Ações de Governança
- **Data de Criação/Publicação:** 10/07/2020
- **Validade:** 50 anos a partir de 1º de janeiro de 2021

## Titularidade e Autoria
- **Titular:** INSTITUTO FEDERAL DE EDUCAÇÃO CIÊNCIA E TECNOLOGIA DO ESPIRITO SANTO (IFES)
- **Autores:** 
  - ANDRÉ ARAÚJO MARTINI
  - THALMO DE PAIVA COELHO JUNIOR

## Especificações Técnicas e Integridade
O sistema utiliza algoritmos de segurança para garantia de integridade, conforme atestado no registro oficial:
- **Algoritmo Hash:** SHA-512
- **Resumo Digital:** \`430c252efdf67678e7f0253ec5303c8e9145a81dc6e02da4eb08dd1330d83a4d8fa91f9db276a1b1a5fe7248f827e69c3fe4808b13d72634aad41a0d649ebbc1\`

---
*Este registro garante a proteção legal do software e o reconhecimento institucional e autoral do MAGO como ferramenta estratégica de governança.*
`;

const CentralAjuda = () => {
    const [activeTab, setActiveTab] = useState(TABS[0].id);
    const [content, setContent] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadContent = async () => {
            const tab = TABS.find(t => t.id === activeTab);
            if (!tab) return;

            // Se for conteúdo estático, carrega diretamente
            if (tab.isStatic) {
                if (tab.id === 'gov-gestao') setContent(GOV_GESTAO_CONTENT);
                if (tab.id === 'registro-ip') setContent(REGISTRO_IP_CONTENT);
                setError(null);
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(null);
            try {
                const response = await fetch(tab.file);
                if (!response.ok) {
                    throw new Error('Não foi possível carregar o documento.');
                }
                const text = await response.text();
                setContent(text);
            } catch (err) {
                console.error("Erro ao carregar documento:", err);
                setError('Erro ao carregar o conteúdo. Tente novamente mais tarde.');
                setContent('');
            } finally {
                setLoading(false);
            }
        };

        loadContent();
    }, [activeTab]);

    return (
        <div className="view-container central-ajuda-view">
            <header className="view-header">
                <h1>Central de Ajuda</h1>
            </header>

            <div className="central-ajuda-layout">
                {/* Menu Sidebar (Desktop) */}
                <aside className="ajuda-sidebar desktop-only">
                    <nav>
                        {TABS.map(tab => (
                            <button
                                key={tab.id}
                                className={`ajuda-nav-btn ${activeTab === tab.id ? 'active' : ''}`}
                                onClick={() => setActiveTab(tab.id)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </aside>

                {/* Menu Dropdown (Mobile) */}
                <div className="ajuda-mobile-menu mobile-only">
                    <label htmlFor="ajuda-nav-select">Selecione o tópico:</label>
                    <select
                        id="ajuda-nav-select"
                        value={activeTab}
                        onChange={(e) => setActiveTab(e.target.value)}
                        className="ajuda-select"
                    >
                        {TABS.map(tab => (
                            <option key={tab.id} value={tab.id}>
                                {tab.label}
                            </option>
                        ))}
                    </select>
                </div>

                <main className="ajuda-content">
                    {loading ? (
                        <Spinner text="Carregando documento..." />
                    ) : error ? (
                        <div className="error-message">{error}</div>
                    ) : (
                        <div className="markdown-body">
                            <ReactMarkdown>{content}</ReactMarkdown>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
};

export default CentralAjuda;
